/**
 * A run parameter declared with `control: "tabs"` — a choice the page shows
 * as tabs above the upload zone instead of a dropdown in the form. A tool
 * with two data sources (drop a workbook / pull from a system) declares one;
 * the rest of the form follows it through `visible_when`, and the upload
 * zone through the input slot's `required_when`.
 *
 * The value is the parameter's effective value: what the operator picked,
 * else its declared default — so the first tab is selected on arrival.
 */
import { Tab, Tabs } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RunParam } from "../lib/api";
import { effectiveValue } from "../lib/params";
import { resolveText } from "../lib/tooltext";

export function SourceTabs({
  param,
  params,
  values,
  onChange,
}: {
  param: RunParam;
  params: RunParam[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
}) {
  const { i18n } = useTranslation();
  const current = effectiveValue(params, values, param.name);
  const value = param.choices.includes(current) ? current : (param.choices[0] ?? "");
  return (
    <Tabs
      value={value}
      onChange={(_e, next: string) => onChange({ ...values, [param.name]: next })}
      aria-label={resolveText(param.label, i18n.language) || param.name}
      sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
    >
      {param.choices.map((choice) => (
        <Tab
          key={choice}
          value={choice}
          label={resolveText(param.choice_labels?.[choice], i18n.language) || choice}
        />
      ))}
    </Tabs>
  );
}
